import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/** Up to this many feature lines are shown on the brand panel. */
export const MAX_AUTH_FEATURES = 3;

export interface AuthFeature {
  /** Font Awesome classes, e.g. `fa-solid fa-ship`. */
  icon: string;
  /** Translation key. */
  text: string;
}

/**
 * The shared login layout (design handoff, "Step 9, streamlined: the login
 * page"): a navy brand panel on the left (logo, headline, up to three
 * feature lines, footer) and a centred 420px column on the right where the
 * page projects its own form. `[authTopRight]` is a slot for the language
 * picker. Below ~900px the brand panel is hidden and `logoUrlOnLight` shows
 * above the form (in dark mode the reversed `logoUrl` instead, since the
 * form side is dark too).
 *
 * It fills the viewport itself (no app shell around it) and is fed with
 * translation keys, so each app supplies its own texts. Styling lives in
 * `@seahorse/theme`'s `_auth.scss`.
 */
@Component({
  selector: 'sh-auth-layout',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule],
  templateUrl: './auth-layout.component.html',
})
export class AuthLayoutComponent {
  /** Logo for the navy panel (reversed). */
  logoUrl = input('');
  /** Logo for the narrow-screen variant, shown on white. */
  logoUrlOnLight = input('');
  /** Translation key. */
  headline = input('');
  features = input<AuthFeature[]>();
  /** Translation key. */
  footer = input('');
  /** Only shown when given; never pass a build timestamp. */
  version = input<string>();

  // A computed: only produces a new array when the `features` input changes.
  protected readonly visibleFeatures = computed(() =>
    (this.features() ?? []).slice(0, MAX_AUTH_FEATURES)
  );
}
